import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Progress, Table, Tag, Button, Input, Spin, Alert } from 'antd';
import { Line } from 'react-chartjs-2';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import {
  DashboardData,
  SKUItem,
  SKUListParams,
  SKUPaginationInfo,
} from '../../../../types/receiver';
import { ADOPTION_COLORS, PROGRESS_COLORS } from '../../../constants/graphColors';
import { JobStatus } from '../../../../types/scorecard';
import { localDateTimeString, getTimezoneOffset } from '../../../utils/Utils';
import { safeNumber } from '../../../utils/NumberUtils';
import ChartErrorBoundary from '../../global/ChartErrorBoundary';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

interface AdoptionTabProps {
  data?: DashboardData;
  loading?: boolean;
  onRefresh?: () => void;
  onFetchAdoptionStats?: (receiverId: number) => Promise<any>;
  receiverId?: number;
  onFetchSkus?: (receiverId: number, params: SKUListParams) => Promise<any>;
  skuData?: { [receiverId: number]: SKUItem[] };
  skuPagination?: { [receiverId: number]: SKUPaginationInfo };
  skuLoading?: { [receiverId: number]: boolean };
  skuSearchError?: { [receiverId: number]: string | null };
  refreshJobStatus?: JobStatus | null;
  onCheckJobStatus?: (jobId: string) => void;
}

const AdoptionTab: React.FC<AdoptionTabProps> = ({
  data,
  loading = false,
  onRefresh,
  onFetchAdoptionStats,
  receiverId,
  onFetchSkus,
  skuData,
  skuPagination,
  skuLoading,
  skuSearchError,
  refreshJobStatus,
  onCheckJobStatus,
}) => {
  const { t } = useTranslation();

  const timezoneName = useMemo(() => getTimezoneOffset(), []);

  const [searchValue, setSearchValue] = useState('');
  const [currentSort, setCurrentSort] = useState<{ field: string; order: string }>({
    field: 'sku',
    order: 'asc',
  });

  const currentSkuData = receiverId ? skuData?.[receiverId] || [] : [];
  const currentPaginationInfo = receiverId
    ? skuPagination?.[receiverId] || {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      }
    : {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
  const currentSkuLoading = receiverId ? skuLoading?.[receiverId] || false : false;
  const currentSkuSearchError = receiverId ? skuSearchError?.[receiverId] || null : null;

  // Unified data loading function
  const loadSkuList = useCallback(
    async (params: SKUListParams) => {
      if (!receiverId || !onFetchSkus) return;

      if (params.sort && params.order) {
        setCurrentSort({ field: params.sort, order: params.order });
      }

      await onFetchSkus(receiverId, params);
    },
    [receiverId, onFetchSkus]
  );

  useEffect(() => {
    if (receiverId && onFetchSkus) {
      loadSkuList({ page: 1, limit: 50 });
    }
  }, [receiverId, onFetchSkus, loadSkuList]);

  useEffect(() => {
    if (!refreshJobStatus || !onCheckJobStatus) return;

    const shouldPoll =
      refreshJobStatus.status === 'queued' ||
      refreshJobStatus.status === 'running' ||
      refreshJobStatus.status === 'processing';
    if (!shouldPoll) return;

    const pollInterval = setInterval(() => {
      onCheckJobStatus(refreshJobStatus.jobId);
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [refreshJobStatus, onCheckJobStatus]);

  // Fetch updated data when refresh job completes successfully
  useEffect(() => {
    if (!refreshJobStatus || !receiverId || !onFetchAdoptionStats) return;

    if (refreshJobStatus.status === 'completed') {
      // Fetch updated adoption data to get new lastUpdated timestamp
      setTimeout(() => {
        onFetchAdoptionStats(receiverId);
      }, 500);
    }
  }, [refreshJobStatus?.status, refreshJobStatus?.jobId, receiverId, onFetchAdoptionStats]);

  // Default empty data structure
  const defaultData: DashboardData = {
    statusMetrics: {
      inPreparation: { count: 0, percentageChange: 0 },
      ready: { count: 0, percentageChange: 0 },
      submitted: { count: 0, percentageChange: 0 },
      received: { count: 0, percentageChange: 0 },
      live: { count: 0, percentageChange: 0 },
    },
    timeSeriesData: [],
    pipelineDistribution: {
      inPreparation: 0,
      ready: 0,
      submitted: 0,
      received: 0,
      live: 0,
    },
    skuManagement: [],
    lastUpdated: t('common:noData'),
  };

  // Safely merge data with defaults to prevent undefined errors
  const adoptionData = {
    statusMetrics: {
      inPreparation: {
        count: safeNumber(
          data?.statusMetrics?.inPreparation?.count,
          defaultData.statusMetrics.inPreparation.count
        ),
        percentageChange: safeNumber(
          data?.statusMetrics?.inPreparation?.percentageChange,
          defaultData.statusMetrics.inPreparation.percentageChange
        ),
      },
      ready: {
        count: safeNumber(data?.statusMetrics?.ready?.count, defaultData.statusMetrics.ready.count),
        percentageChange: safeNumber(
          data?.statusMetrics?.ready?.percentageChange,
          defaultData.statusMetrics.ready.percentageChange
        ),
      },
      submitted: {
        count: safeNumber(
          data?.statusMetrics?.submitted?.count,
          defaultData.statusMetrics.submitted.count
        ),
        percentageChange: safeNumber(
          data?.statusMetrics?.submitted?.percentageChange,
          defaultData.statusMetrics.submitted.percentageChange
        ),
      },
      received: {
        count: safeNumber(
          data?.statusMetrics?.received?.count,
          defaultData.statusMetrics.received.count
        ),
        percentageChange: safeNumber(
          data?.statusMetrics?.received?.percentageChange,
          defaultData.statusMetrics.received.percentageChange
        ),
      },
      live: {
        count: safeNumber(data?.statusMetrics?.live?.count, defaultData.statusMetrics.live.count),
        percentageChange: safeNumber(
          data?.statusMetrics?.live?.percentageChange,
          defaultData.statusMetrics.live.percentageChange
        ),
      },
    },
    timeSeriesData: data?.timeSeriesData || defaultData.timeSeriesData,
    pipelineDistribution: {
      inPreparation: safeNumber(
        data?.pipelineDistribution?.inPreparation,
        defaultData.pipelineDistribution.inPreparation
      ),
      ready: safeNumber(data?.pipelineDistribution?.ready, defaultData.pipelineDistribution.ready),
      submitted: safeNumber(
        data?.pipelineDistribution?.submitted,
        defaultData.pipelineDistribution.submitted
      ),
      received: safeNumber(
        data?.pipelineDistribution?.received,
        defaultData.pipelineDistribution.received
      ),
      live: safeNumber(data?.pipelineDistribution?.live, defaultData.pipelineDistribution.live),
    },
    skuManagement: data?.skuManagement || defaultData.skuManagement,
    lastUpdated: data?.lastUpdated || defaultData.lastUpdated,
  };

  // Handler functions for pagination
  const handlePageChange = (page: number, pageSize?: number) => {
    let params: SKUListParams = {
      page,
      limit: pageSize || currentPaginationInfo.limit,
      sort: currentSort.field,
      order: currentSort.order,
    };

    // If we have a search term, include it
    if (searchValue.trim()) {
      const searchParams = parseSearchQuery(searchValue);
      params = { ...params, ...searchParams };
    }

    loadSkuList(params);
  };

  const handlePageSizeChange = (current: number, size: number) => {
    let params: SKUListParams = {
      page: searchValue.trim() ? 1 : current,
      limit: size,
      sort: currentSort.field,
      order: currentSort.order,
    };

    // If we have a search term, include it
    if (searchValue.trim()) {
      const searchParams = parseSearchQuery(searchValue);
      params = { ...params, ...searchParams };
    }

    loadSkuList(params);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let params: SKUListParams = {
      page: pagination.current,
      limit: pagination.pageSize,
      sort: sorter.field || 'sku',
      order: sorter.order === 'descend' ? 'desc' : 'asc',
    };

    // If we have a search term, include it
    if (searchValue.trim()) {
      const searchParams = parseSearchQuery(searchValue);
      params = { ...params, ...searchParams };
    }

    loadSkuList(params);
  };

  const parseSearchQuery = (searchTerm: string) => {
    if (!searchTerm.trim()) return {};

    const trimmedSearch = searchTerm.trim();
    const params: any = {};

    params.search = trimmedSearch;

    return params;
  };

  const handleSkuSearch = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      loadSkuList({ page: 1, limit: currentPaginationInfo.limit });
      return;
    }

    const searchParams = parseSearchQuery(searchTerm);
    loadSkuList({ ...searchParams, page: 1, limit: 50 });
  };

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);

    // Use new endpoint - trigger search immediately for exact SKU lookup
    if (value.length >= 3) {
      handleSkuSearch(value);
    } else if (!value.trim()) {
      // Clear search - return to browse mode
      handleSkuSearch('');
    }
  };

  const handleRefresh = async () => {
    if (!receiverId) return;

    try {
      await Promise.all([
        onRefresh?.(),
        onFetchAdoptionStats?.(receiverId),
        onFetchSkus?.(receiverId, {
          page: currentPaginationInfo.page,
          limit: currentPaginationInfo.limit,
          sort: currentSort.field,
          order: currentSort.order,
          ...(searchValue.trim() && parseSearchQuery(searchValue)),
        }),
      ]);
    } catch (error) {
      //
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Preparation':
        return 'default';
      case 'Ready':
        return 'blue';
      case 'Submitted':
        return 'orange';
      case 'Received':
        return 'cyan';
      case 'Live':
        return 'green';
      default:
        return 'default';
    }
  };

  const tableData = currentSkuData;

  // Chart configuration with error handling
  const createChartData = (): ChartData<'line'> => {
    try {
      const timeSeriesData = adoptionData.timeSeriesData || [];
      return {
        labels: timeSeriesData.map(point => {
          const date = new Date(point?.date || Date.now());
          return `${date.getMonth() + 1}/${date.getDate()}`;
        }),
        datasets: [
          {
            label: t('receiver:labels.inPreparation'),
            data: timeSeriesData.map(point => point?.inPreparation || 0),
            borderColor: ADOPTION_COLORS.IN_PREPARATION,
            backgroundColor: ADOPTION_COLORS.IN_PREPARATION,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: ADOPTION_COLORS.IN_PREPARATION,
            pointBorderColor: ADOPTION_COLORS.WHITE,
            pointBorderWidth: 1,
          },
          {
            label: t('receiver:labels.ready'),
            data: timeSeriesData.map(point => point?.ready || 0),
            borderColor: ADOPTION_COLORS.READY,
            backgroundColor: ADOPTION_COLORS.READY,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: ADOPTION_COLORS.READY,
            pointBorderColor: ADOPTION_COLORS.WHITE,
            pointBorderWidth: 1,
          },
          {
            label: t('receiver:labels.submitted'),
            data: timeSeriesData.map(point => point?.submitted || 0),
            borderColor: ADOPTION_COLORS.SUBMITTED,
            backgroundColor: ADOPTION_COLORS.SUBMITTED,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: ADOPTION_COLORS.SUBMITTED,
            pointBorderColor: ADOPTION_COLORS.WHITE,
            pointBorderWidth: 1,
          },
          {
            label: t('receiver:labels.received'),
            data: timeSeriesData.map(point => point?.received || 0),
            borderColor: ADOPTION_COLORS.RECEIVED,
            backgroundColor: ADOPTION_COLORS.RECEIVED,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: ADOPTION_COLORS.RECEIVED,
            pointBorderColor: ADOPTION_COLORS.WHITE,
            pointBorderWidth: 1,
          },
          {
            label: t('receiver:labels.live'),
            data: timeSeriesData.map(point => point?.live || 0),
            borderColor: ADOPTION_COLORS.LIVE,
            backgroundColor: ADOPTION_COLORS.LIVE,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: ADOPTION_COLORS.LIVE,
            pointBorderColor: ADOPTION_COLORS.WHITE,
            pointBorderWidth: 1,
          },
        ],
      };
    } catch (error) {
      return {
        labels: [],
        datasets: [],
      };
    }
  };

  const chartData = createChartData();

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          padding: 12,
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback(value) {
            return value;
          },
        },
      },
      x: {
        grid: {
          display: true,
          color: ADOPTION_COLORS.BORDER_LIGHT,
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };

  const tableColumns = [
    {
      title: t('receiver:tableColumns.sku'),
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: t('receiver:tableColumns.retailerSku'),
      dataIndex: 'retailerSku',
      key: 'retailerSku',
      render: (text: string) => text || '-',
    },
    {
      title: t('receiver:tableColumns.partName'),
      dataIndex: 'partName',
      key: 'partName',
      render: (text: string) => (
        <div>
          {text ? (
            <div style={{ fontWeight: 500 }}>{text}</div>
          ) : (
            <div
              style={{ fontSize: 12, color: ADOPTION_COLORS.TEXT_SECONDARY, fontStyle: 'italic' }}
            >
              {t('receiver:noPartTypeSelected')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('receiver:tableColumns.brand'),
      dataIndex: 'brand',
      key: 'brand',
      render: (brand: string, record: any) => {
        if (record.brandName) {
          return `${record.brandName} (${brand})`;
        }
        return brand;
      },
    },
    {
      title: t('receiver:tableColumns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: `${t('receiver:tableColumns.lastUpdated')} (${timezoneName})`,
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      render: (text: string) => localDateTimeString(text),
    },
  ];

  const renderJobStatus = () => {
    if (!refreshJobStatus) return null;

    const { status, message, progress, estimatedCompletion, completedAt, error } = refreshJobStatus;

    if (status === 'queued') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <ClockCircleOutlined className="text-blue-500" />
              <span className="text-sm">{t('receiver:refreshQueued')}</span>
            </div>
          }
          description={
            estimatedCompletion && (
              <div className="text-xs text-gray-600">
                {t('receiver:estimatedCompletion')}:{' '}
                {new Date(estimatedCompletion).toLocaleTimeString()}
              </div>
            )
          }
          type="info"
          showIcon={false}
          className="mb-3"
        />
      );
    }

    if (status === 'running' || status === 'processing') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <Spin size="small" />
              <span className="text-sm">{t('receiver:refreshInProgress')}</span>
            </div>
          }
          description={
            <div className="mt-2">
              {progress !== undefined && (
                <Progress
                  percent={Math.round(progress)}
                  size="small"
                  status="active"
                  className="mb-2"
                />
              )}
              <div className="text-xs text-gray-600">{message}</div>
            </div>
          }
          type="info"
          showIcon={false}
          className="mb-3"
        />
      );
    }

    if (status === 'completed') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-500" />
              <span className="text-sm">{t('receiver:refreshCompleted')}</span>
            </div>
          }
          description={
            completedAt && (
              <div className="text-xs text-gray-600">
                {t('receiver:completedAt')}: {new Date(completedAt).toLocaleTimeString()}
              </div>
            )
          }
          type="success"
          showIcon={false}
          className="mb-3"
          closable
        />
      );
    }

    if (status === 'failed') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <CloseCircleOutlined className="text-red-500" />
              <span className="text-sm">{t('receiver:refreshFailed')}</span>
            </div>
          }
          description={error && <div className="text-xs text-red-600">{error}</div>}
          type="error"
          showIcon={false}
          className="mb-3"
          closable
        />
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spin size="large" />
        <div className="ml-2">{t('receiver:loadingAdoptionData')}</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Fixed header section */}
      <div className="px-3 pt-3 flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 style={{ margin: 0 }}>{t('receiver:adoptionDashboard')}</h3>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button
              type="primary"
              onClick={handleRefresh}
              loading={
                loading ||
                refreshJobStatus?.status === 'queued' ||
                refreshJobStatus?.status === 'running' ||
                refreshJobStatus?.status === 'processing'
              }
              size="small"
              icon={<ReloadOutlined />}
              disabled={
                refreshJobStatus?.status === 'queued' ||
                refreshJobStatus?.status === 'running' ||
                refreshJobStatus?.status === 'processing'
              }
            >
              {t('receiver:refreshData')}
            </Button>
            <div className="text-xs text-gray-700">
              {t('receiver:lastUpdated')} {adoptionData.lastUpdated}
            </div>
          </div>
        </div>

        {renderJobStatus()}
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-auto px-3 pb-3">
        {/* Status Cards */}
        <Row gutter={[12, 12]} className="mb-4">
          <Col span={5}>
            <Card size="small" bodyStyle={{ padding: '8px' }}>
              <div style={{ fontSize: 10, color: ADOPTION_COLORS.TEXT_SECONDARY, marginBottom: 4 }}>
                {t('receiver:labels.inPreparationCaps')}
              </div>
              <div
                style={{ fontSize: 18, fontWeight: 'bold', color: ADOPTION_COLORS.TEXT_PRIMARY }}
              >
                {adoptionData.statusMetrics.inPreparation.count}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  color:
                    adoptionData.statusMetrics.inPreparation.percentageChange >= 0
                      ? ADOPTION_COLORS.SUCCESS
                      : ADOPTION_COLORS.ERROR,
                }}
              >
                {adoptionData.statusMetrics.inPreparation.percentageChange >= 0 ? '+' : ''}
                {adoptionData.statusMetrics.inPreparation.percentageChange}%
              </div>
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small" bodyStyle={{ padding: '8px' }}>
              <div style={{ fontSize: 10, color: ADOPTION_COLORS.TEXT_SECONDARY, marginBottom: 4 }}>
                {t('receiver:labels.readyCaps')}
              </div>
              <div
                style={{ fontSize: 18, fontWeight: 'bold', color: ADOPTION_COLORS.TEXT_PRIMARY }}
              >
                {adoptionData.statusMetrics.ready.count}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  color:
                    adoptionData.statusMetrics.ready.percentageChange >= 0
                      ? ADOPTION_COLORS.SUCCESS
                      : ADOPTION_COLORS.ERROR,
                }}
              >
                {adoptionData.statusMetrics.ready.percentageChange >= 0 ? '+' : ''}
                {adoptionData.statusMetrics.ready.percentageChange}%
              </div>
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small" bodyStyle={{ padding: '8px' }}>
              <div style={{ fontSize: 10, color: ADOPTION_COLORS.TEXT_SECONDARY, marginBottom: 4 }}>
                {t('receiver:labels.submittedCaps')}
              </div>
              <div
                style={{ fontSize: 18, fontWeight: 'bold', color: ADOPTION_COLORS.TEXT_PRIMARY }}
              >
                {adoptionData.statusMetrics.submitted.count}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  color:
                    adoptionData.statusMetrics.submitted.percentageChange >= 0
                      ? ADOPTION_COLORS.SUCCESS
                      : ADOPTION_COLORS.ERROR,
                }}
              >
                {adoptionData.statusMetrics.submitted.percentageChange >= 0 ? '+' : ''}
                {adoptionData.statusMetrics.submitted.percentageChange}%
              </div>
            </Card>
          </Col>
          <Col span={5}>
            <Card size="small" bodyStyle={{ padding: '8px' }}>
              <div style={{ fontSize: 10, color: ADOPTION_COLORS.TEXT_SECONDARY, marginBottom: 4 }}>
                {t('receiver:labels.receivedCaps')}
              </div>
              <div
                style={{ fontSize: 18, fontWeight: 'bold', color: ADOPTION_COLORS.TEXT_PRIMARY }}
              >
                {adoptionData.statusMetrics.received.count}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  color:
                    adoptionData.statusMetrics.received.percentageChange >= 0
                      ? ADOPTION_COLORS.SUCCESS
                      : ADOPTION_COLORS.ERROR,
                }}
              >
                {adoptionData.statusMetrics.received.percentageChange >= 0 ? '+' : ''}
                {adoptionData.statusMetrics.received.percentageChange}%
              </div>
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small" bodyStyle={{ padding: '8px' }}>
              <div style={{ fontSize: 10, color: ADOPTION_COLORS.TEXT_SECONDARY, marginBottom: 4 }}>
                {t('receiver:labels.liveCaps')}
              </div>
              <div
                style={{ fontSize: 18, fontWeight: 'bold', color: ADOPTION_COLORS.TEXT_PRIMARY }}
              >
                {adoptionData.statusMetrics.live.count}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  color:
                    adoptionData.statusMetrics.live.percentageChange >= 0
                      ? ADOPTION_COLORS.SUCCESS
                      : ADOPTION_COLORS.ERROR,
                }}
              >
                {adoptionData.statusMetrics.live.percentageChange >= 0 ? '+' : ''}
                {adoptionData.statusMetrics.live.percentageChange}%
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]} className="mb-4">
          {/* Adoption Over Time Chart */}
          <Col span={14}>
            <Card title={t('receiver:adoptionOverTime')} size="small">
              <div style={{ height: 200, padding: '5px 0' }}>
                {adoptionData.timeSeriesData && adoptionData.timeSeriesData.length > 0 ? (
                  <ChartErrorBoundary>
                    <Line data={chartData} options={chartOptions} />
                  </ChartErrorBoundary>
                ) : (
                  <div
                    className="flex items-center justify-center h-full"
                    style={{ color: ADOPTION_COLORS.TEXT_SECONDARY }}
                  >
                    {t('receiver:noDataAvailable')}
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* Pipeline Distribution */}
          <Col span={10}>
            <Card title={t('receiver:pipelineDistribution')} size="small">
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs">{t('receiver:labels.inPreparation')}</span>
                  <span className="text-xs">
                    {adoptionData.pipelineDistribution.inPreparation}%
                  </span>
                </div>
                <Progress
                  percent={adoptionData.pipelineDistribution.inPreparation}
                  strokeColor={PROGRESS_COLORS.IN_PREPARATION}
                  showInfo={false}
                  size="small"
                />
              </div>
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs">{t('receiver:labels.ready')}</span>
                  <span className="text-xs">{adoptionData.pipelineDistribution.ready}%</span>
                </div>
                <Progress
                  percent={adoptionData.pipelineDistribution.ready}
                  strokeColor={PROGRESS_COLORS.READY}
                  showInfo={false}
                  size="small"
                />
              </div>
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs">{t('receiver:labels.submitted')}</span>
                  <span className="text-xs">{adoptionData.pipelineDistribution.submitted}%</span>
                </div>
                <Progress
                  percent={adoptionData.pipelineDistribution.submitted}
                  strokeColor={PROGRESS_COLORS.SUBMITTED}
                  showInfo={false}
                  size="small"
                />
              </div>
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="text-xs">{t('receiver:labels.received')}</span>
                  <span className="text-xs">{adoptionData.pipelineDistribution.received}%</span>
                </div>
                <Progress
                  percent={adoptionData.pipelineDistribution.received}
                  strokeColor={PROGRESS_COLORS.RECEIVED}
                  showInfo={false}
                  size="small"
                />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span className="text-xs">{t('receiver:labels.live')}</span>
                  <span className="text-xs">{adoptionData.pipelineDistribution.live}%</span>
                </div>
                <Progress
                  percent={adoptionData.pipelineDistribution.live}
                  strokeColor={PROGRESS_COLORS.LIVE}
                  showInfo={false}
                  size="small"
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* SKU Management Table */}
        <Card
          title={t('receiver:skuManagement')}
          size="small"
          extra={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Input
                placeholder={t('receiver:searchByExactSku')}
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={e => handleSearchValueChange(e.target.value)}
                style={{ width: 250 }}
                size="small"
              />
              {currentSkuSearchError && <span className="red">{currentSkuSearchError}</span>}
              <span className="text-xs ml-2" style={{ color: ADOPTION_COLORS.TEXT_SECONDARY }}>
                {searchValue.trim()
                  ? `${currentPaginationInfo.total} ${t('receiver:searchResults')}`
                  : `${currentPaginationInfo.total} ${t('receiver:totalSkus')}`}
              </span>
            </div>
          }
        >
          <Table
            dataSource={tableData}
            columns={tableColumns}
            pagination={{
              current: currentPaginationInfo.page,
              pageSize: currentPaginationInfo.limit,
              total: currentPaginationInfo.total,
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Part Numbers`,
              size: 'small',
              onChange: handlePageChange,
              onShowSizeChange: handlePageSizeChange,
            }}
            onChange={handleTableChange}
            size="small"
            rowKey="sku"
            loading={currentSkuLoading}
            locale={{
              emptyText: (() => {
                if (currentSkuSearchError) {
                  return currentSkuSearchError;
                }
                if (searchValue.trim()) {
                  return t('receiver:noMatchingSkus');
                }
                return currentPaginationInfo.total === 0
                  ? t('receiver:noSkuData')
                  : t('receiver:noMatchingSkus');
              })(),
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default AdoptionTab;
